// Restaurant Sales Report — Tally voucher CSV + readable summary, by month/range.
import React, { useState } from 'react';
import { restaurantAPI } from '../../services/api';

// Restaurant Sales Report — download sales as a Tally voucher CSV or a readable
// summary CSV, for a selected month or a custom date range. Backend aggregates
// delivered orders per IST-day × payment mode (CASH / ONLINE).

function firstOfThisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthRange(ym) {
  const [y, m] = ym.split('-').map(Number);
  const from = `${ym}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const to = `${ym}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function SalesReport() {
  const [mode, setMode] = useState('month'); // 'month' | 'range'
  const [month, setMonth] = useState(firstOfThisMonth());
  const [from, setFrom] = useState(`${firstOfThisMonth()}-01`);
  const [to, setTo] = useState(todayStr());
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const resolveRange = () => (mode === 'month' ? monthRange(month) : { from, to });

  const download = async (format) => {
    setError('');
    const { from: f, to: t } = resolveRange();
    if (!f || !t) { setError('Please choose a valid date range.'); return; }
    if (f > t) { setError('"From" date must be on or before "To" date.'); return; }
    setBusy(format);
    try {
      const res = await restaurantAPI.downloadSalesReport({ from: f, to: t, format });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `sales_${f}_to_${t}_${format}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Error body comes back as a blob (responseType blob) — read it for the message.
      let msg = 'Failed to generate the report.';
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          msg = JSON.parse(txt).message || msg;
        } else {
          msg = e.response?.data?.message || e.message || msg;
        }
      } catch (_) {}
      setError(msg);
    } finally {
      setBusy('');
    }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.title}>Sales Report</h1>
        <p style={s.subtitle}>Download your sales for a month or a custom date range — as a Tally voucher file or a readable summary.</p>
      </div>

      <div style={s.card}>
        {/* Mode toggle */}
        <div style={s.toggleRow}>
          <button style={{ ...s.toggle, ...(mode === 'month' ? s.toggleOn : {}) }} onClick={() => setMode('month')}>By Month</button>
          <button style={{ ...s.toggle, ...(mode === 'range' ? s.toggleOn : {}) }} onClick={() => setMode('range')}>Custom Range</button>
        </div>

        {mode === 'month' ? (
          <div style={s.field}>
            <label style={s.label}>Month</label>
            <input type="month" value={month} max={firstOfThisMonth()} onChange={(e) => setMonth(e.target.value)} style={s.input} />
          </div>
        ) : (
          <div style={s.rangeRow}>
            <div style={s.field}>
              <label style={s.label}>From</label>
              <input type="date" value={from} max={to || todayStr()} onChange={(e) => setFrom(e.target.value)} style={s.input} />
            </div>
            <div style={s.field}>
              <label style={s.label}>To</label>
              <input type="date" value={to} max={todayStr()} onChange={(e) => setTo(e.target.value)} style={s.input} />
            </div>
          </div>
        )}

        {error ? <div style={s.error}>{error}</div> : null}

        <div style={s.btnRow}>
          <button style={{ ...s.btn, ...s.btnDark }} disabled={!!busy} onClick={() => download('tally')}>
            {busy === 'tally' ? 'Preparing…' : '⬇ Download for Tally (CSV)'}
          </button>
          <button style={{ ...s.btn, ...s.btnLight }} disabled={!!busy} onClick={() => download('summary')}>
            {busy === 'summary' ? 'Preparing…' : '⬇ Download Summary (CSV)'}
          </button>
        </div>
      </div>

      <div style={s.info}>
        <div style={s.infoTitle}>What's in each file?</div>
        <ul style={s.infoList}>
          <li><b>Tally file</b> — one sales voucher per day, split by payment mode (CASH / ONLINE), item-wise with CGST/SGST. Ready to import into Tally.</li>
          <li><b>Summary file</b> — a quick read: totals by payment mode, plus item-wise quantity, sales and GST for the whole period.</li>
          <li>Only <b>delivered</b> orders are counted, by their delivery date (IST). Cancelled orders are excluded.</li>
        </ul>
      </div>
    </div>
  );
}

const s = {
  container: { padding: 20, maxWidth: 720, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px' },
  subtitle: { fontSize: 14, color: '#666', margin: 0 },
  card: { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  toggleRow: { display: 'flex', gap: 8, marginBottom: 18 },
  toggle: { flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer' },
  toggleOn: { background: '#111827', color: '#fff', borderColor: '#111827' },
  field: { marginBottom: 14, flex: 1 },
  rangeRow: { display: 'flex', gap: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' },
  error: { background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', borderRadius: 8, padding: '10px 12px', fontSize: 13, margin: '4px 0 14px' },
  btnRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 },
  btn: { flex: '1 1 220px', padding: '12px 18px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnDark: { background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)', color: '#fff' },
  btnLight: { background: '#f0f0f0', color: '#333' },
  info: { background: '#F0F7FF', border: '1px solid #CCE0FF', borderRadius: 10, padding: 16 },
  infoTitle: { fontSize: 14, fontWeight: 600, color: '#0066CC', marginBottom: 8 },
  infoList: { fontSize: 13, color: '#444', lineHeight: 1.7, margin: 0, paddingLeft: 18 },
};

export default SalesReport;
